import { IHttpResponse, IHttpService } from 'angular';
import * as _ from 'underscore';

import { ListResult } from '../../../ts/fetchers/ListResult';
import { CollectionMapper } from '../../../ts/mappers/CollectionMapper';
import { BaseParamsBuilderResourceQuery } from '../../../ts/resources/ResourceQuery';
import { Search, SearchResult } from '../../../ts/resources/Search';
import { FilterOps, ILeafFilterClause, IStringValuedFieldFilterClause, IStringValuedFilterClause } from '../../../ts/ServerApi/types/IFilterClause';
import { IGetItemsResult } from '../../../ts/ServerApi/types/IGetItemsResult';
import { IOrderClause } from '../../../ts/ServerApi/types/IOrderClause';
import { appStore } from '../../AppStore';
import { CompositeSessionInfoSelectors, SessionSiteSelectors, SessionUserSelectors } from '../../selectors/SessionInfoSelectors';
import { ServiceLocator } from '../../ServiceLocator';
import { Collection, CollectionItem } from './CollectionModels';
import { ICollectionInfo } from './ICollectionInfo';

// Change endpoint to absolute path if you want to run a mixed local scenario
// e.g. local - 'http://localhost:8080/content-collections/collections'
//      server -  'content-collections/collections' // This won't work until we've integrated service into TSM
// Do not merge with the local URI or integration tests will fail
// const COLLECTIONS_URI: string = 'http://localhost:8080/content-collections/collections';
const COLLECTIONS_URI: string = 'content-collections/collections';

export interface GetCollectionsResponse extends IGetItemsResult {
  readonly items?: Collection[];
}

const FilterOpsToString = (op: string): string => {
  if (op === FilterOps.eq) {
    return ':';
  } else if (op === FilterOps.gte) {
    return '>';
  } else if (op === FilterOps.lte) {
    return '<';
  }
};

export class CollectionsResourceQuery extends BaseParamsBuilderResourceQuery<ICollectionInfo> {
  public getSlice(start: number, size: number): ng.IPromise<ListResult<ICollectionInfo>> {
    const http = ServiceLocator.get<IHttpService>('$http');

    const filters: ILeafFilterClause[] = this.getFilters();
    const matchesFilter = filters.find((clause) => clause.operator === FilterOps.matches);
    let filterClause = '';
    if (filters.length > 0) {
      filterClause =
        _.without(filters, matchesFilter)
        .map((clause) => {
          const valueClause = clause as IStringValuedFieldFilterClause;
          return valueClause.field + FilterOpsToString(valueClause.operator) + '"' + valueClause.value + '"';
        })
        .join(' AND ');
    }

    if (matchesFilter) {
      const contentTypeClause = 'type:collection';
      if (filterClause.length > 0) {
        filterClause = ' AND ' + filterClause;
      }
      let search = new Search()
        .withOrder(this.getOrder())
        .page(start)
        .limit(size)
        .search((matchesFilter as IStringValuedFilterClause).value)
        .filter(contentTypeClause + filterClause);
      return search.getResults().then((searchResult: SearchResult) => {
        return {
          items: searchResult.items as any[],
          total: searchResult.total,
          index: searchResult.pageIndex
        };
      });
    }

    let orderClause = '';
    const order: IOrderClause[] = this.getOrder();
    if (order.length > 0) {
      orderClause = '&order=' +
        order
          .map((clause) => clause.field + ' ' + (clause.ascending ? 'asc' : 'desc'))
          .join(',');
    }

    let pagingClause = '';
    if (start != null) {
      pagingClause += '&startIndex=' + start;
    }
    if (size != null) {
      pagingClause += '&maxItems=' + size;
    }

    if (filterClause.length > 0) {
      filterClause = '&filter=' + filterClause;
    }

    const url = COLLECTIONS_URI + getUserIdAndSiteIdAsParams() + orderClause + filterClause + pagingClause;
    const requestConfig: ng.IRequestConfig = {
      method: 'GET',
      url
    };

    return http(requestConfig).then((response: IHttpResponse<GetCollectionsResponse>) => {
      return CollectionMapper.mapList(response, start);
    });
  }

  public create(name: string): ng.IPromise<ICollectionInfo> {
    const http = ServiceLocator.get<IHttpService>('$http');

    const url = COLLECTIONS_URI + getUserIdAndSiteIdAsParams();
    const requestConfig: ng.IRequestConfig = {
      method: 'POST',
      url,
      data: { name, desc: ''}
    };

    return http(requestConfig).then(CollectionMapper.mapsSingle);
  }

  public delete(id: string): ng.IPromise<IHttpResponse<{}>>{
    const http = ServiceLocator.get<IHttpService>('$http');

    const url = COLLECTIONS_URI + '/' + id + getUserIdAndSiteIdAsParams();
    const requestConfig: ng.IRequestConfig = {
      method: 'DELETE',
      url,
    };

    return http(requestConfig);
  }

  public getCount(): ng.IPromise<number> {
    throw new Error('CollectionsResourceQuery getCount not implemented');
  }

  public getFirst(): ng.IPromise<ICollectionInfo> {
    throw new Error('CollectionsResourceQuery getFirst not implemented');
  }
}

export class CollectionItemsResourceQuery extends BaseParamsBuilderResourceQuery<any> {

  public constructor(private collectionId: number) {
    super();
  }

  public getSlice(start: number, size: number): ng.IPromise<ListResult<any>> {
    const http = ServiceLocator.get<IHttpService>('$http');

    const url = COLLECTIONS_URI + '/' + this.collectionId + getUserIdAndSiteIdAsParams();
    const requestConfig: ng.IRequestConfig = {
      method: 'GET',
      url
    };

    return http(requestConfig).then((response: IHttpResponse<CollectionItem[]>) => {

      let queryString = '';
      const individualQuerries: string[] = response.data.map((item: CollectionItem) => {
        return '(id:' + item.contentId + ' and type:' + item.contentType + ')';
      });
      queryString = individualQuerries.join(' or ');

      let search = new Search();
      search = search.search(queryString);

      return search.getResults();
    }).then((searchResult: SearchResult) => {
      return {
        items: searchResult.items as any[],
        total: searchResult.total,
        index: searchResult.pageIndex
      };
    });
  }

  public getCount(): ng.IPromise<number> {
    throw new Error('CollectionsResourceQuery getCount not implemented');
  }

  public getFirst(): ng.IPromise<ICollectionInfo> {
    throw new Error('CollectionsResourceQuery getFirst not implemented');
  }
}

function getUserIdAndSiteIdAsParams() {
  const userId = SessionUserSelectors.sessionUserId(appStore.getState());
  const siteId = SessionSiteSelectors.siteId(appStore.getState());

  const params = new Array<string>();
  params.push(`user=${userId}`);
  params.push(`site=${siteId}`);
  if (CompositeSessionInfoSelectors.currentUserIsSiteOrServerAdminSelector(appStore.getState())) {
    params.push(`admin=true`);
  }

  return '?' + params.join('&');
}
